import {Grid} from "@mui/material";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {ElementDto} from "../../../../../common/apis/element/ElementDto";
import {StereotypeApiClient} from "../../../../../common/apis/stereotype/StereotypeApiClient";
import {StereotypeCreateDto, StereotypeDto} from "../../../../../common/apis/stereotype/StereotypeDto";
import {ValidationError} from "../../../../../common/ValidationError";
import {Button} from "../../../../../components/button/Button";
import {NegativeButtons, PositiveButtons} from "../../../../../components/button/ButtonLayout";
import {CancelButton} from "../../../../../components/button/CancelButton";
import Dialog from "../../../../../components/dialogs/Dialog";
import DialogActions from "../../../../../components/dialogs/DialogActions";
import DialogContent from "../../../../../components/dialogs/DialogContent";
import DialogTitle from "../../../../../components/dialogs/DialogTitle";
import SingleSelectField from "../../../../../components/fields/select/SingleSelectField";
import SectionHeader from "../../../../../components/section/SectionHeader";
import {t} from "../../../../../store/localization/TranslMessasge";
import {CommonTranslationKey} from "../../CommonTranslationKey";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import Snackbar from "../../snackbar/Snackbar";
import {StereotypeErrorCode} from "../../stereotypes/StereotypeErrorCodes";
import {useFindAllStereotypesQuery} from "../../stereotypes/StereotypeQuery";
import {ElementTranslationKey} from "../ElementTranslationKey";
import StereotypeBulkCreateGrid from "./StereotypeBulkCreateGrid";

interface StereotypeBulkCreateFromPropertyDialogProps {
    open: boolean,
    onClose: () => void,
    elements: ElementDto[]
}

export default function StereotypeBulkCreateFromPropertyDialog(props: Readonly<StereotypeBulkCreateFromPropertyDialogProps>) {
    const {open, onClose, elements} = props;

    const [selectedPropertyName, setSelectedPropertyName] = useState<string | null>(null);

    const [stereotypes, setStereotypes] = useState<StereotypeCreateDto[]>([]);

    const stereotypeApiClient = useRef(new StereotypeApiClient());
    const {data: existingStereotypes} = useFindAllStereotypesQuery();

    useEffect(() => {
        if (open) {
            return () => {
                setSelectedPropertyName(null);
                setStereotypes([]);
            }
        }
    }, [open, elements]);

    function loadStereotypesFromProperty() {
        if (selectedPropertyName && elements.length > 0) {
            const extractedStereotypes = extractStereotypesFromProperty(selectedPropertyName, elements, existingStereotypes ?? []);
            if (extractedStereotypes.length > 0) {
                setStereotypes(extractedStereotypes);
            } else {
                Snackbar.warning(t(ElementTranslationKey.NO_STEREOTYPES_TO_CREATE_FOUND));
                setStereotypes([]);
            }
        } else {
            setStereotypes([]);
        }
    }

    const propertyNames: string[] = useMemo(() => {
        if (open && elements.length > 0) {
            const propNames = new Set(elements.flatMap(element => element.properties ?? [])
                    .map(property => property.definition.name));

            return Array.from(propNames)
                    .sort( (a, b) => a.localeCompare(b));
        } else {
            return [];
        }
    }, [open, elements]);

    function createStereotypes() {
        if (stereotypes.length > 0) {
            stereotypeApiClient.current.batchCreateStereotypes(stereotypes)
                    .then(() => {
                        Snackbar.success(t(CommonTranslationKey.DATA_SAVED_SUCCESSFULLY))
                        onClose();
                    })
                    .catch((error) => {
                        if (error instanceof ValidationError && error.error.code === StereotypeErrorCode.CONFLICTING_NAME) {
                            Snackbar.warning(t(ElementTranslationKey.STEREOTYPE_WITH_GIVEN_NAME_ALREADY_EXISTS,
                                    {stereotypeName: error.error.arguments?.stereotypeName}));
                        } else {
                            Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error);
                        }
                    });
        }
    }

    function onRemoveStereotype(stereotypeToRemove: StereotypeCreateDto) {
        setStereotypes(stereotypes.filter(stereotype => stereotype.name !== stereotypeToRemove.name));
    }

    return (
        <>
            <Dialog open={open}
                    aria-labelledby="dialog-title"
                    onClose={onClose}
                    maxWidth={'md'}>
                <DialogTitle id="dialog-title"
                             title={t(ElementTranslationKey.CREATE_STEREOTYPES_FROM_PROPERTY_VALUE)}
                             onDialogClosed={onClose}/>
                <DialogContent>
                    <Grid container spacing={2} mt={0}>
                        <Grid item xs>
                            <SingleSelectField
                                    label={t(ElementTranslationKey.PROPERTY_NAME)}
                                    value={selectedPropertyName}
                                    onChange={setSelectedPropertyName}
                                    options={propertyNames}
                                    getOptionLabel={(prop) => prop}/>
                        </Grid>
                        <Grid item xs={"auto"}>
                            <Button label={t(ElementTranslationKey.LOAD_VALUES)}
                                    onClick={loadStereotypesFromProperty}
                                    variant={"contained"}
                                    disabled={!selectedPropertyName}/>
                        </Grid>

                        {stereotypes.length > 0 &&
                                <>
                                    <Grid item xs={12}>
                                        <SectionHeader title={t(ElementTranslationKey.STEREOTYPES_TO_CREATE)} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <StereotypeBulkCreateGrid
                                                stereotypes={stereotypes}
                                                onDelete={onRemoveStereotype}
                                        />
                                    </Grid>
                                </>
                        }
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <NegativeButtons>
                        <CancelButton onClick={onClose}/>
                    </NegativeButtons>
                    <PositiveButtons>
                        <Button label={t(CommonTranslationKey.CREATE)}
                                variant={"contained"}
                                onClick={createStereotypes}
                                disabled={stereotypes.length === 0}/>
                    </PositiveButtons>
                </DialogActions>
            </Dialog>
        </>
    );
}

function extractStereotypesFromProperty(propertyName: string, elements: ElementDto[], existingStereotypes: StereotypeDto[]): StereotypeCreateDto[] {
    const stereotypes: Map<string, StereotypeCreateDto> = new Map();

    for (const element of elements) {
        const property = element.properties?.find(prop => prop.definition.name === propertyName);
        if (property && (property.value ?? "").trim() !== "") {
            const stereotypeName = (property.value ?? "").trim();
            if (existingStereotypes.some(existingStereotype => existingStereotype.name === stereotypeName)) {
                continue;
            }

            if (!stereotypes.has(stereotypeName)) {
                stereotypes.set(stereotypeName, {
                    name: stereotypeName,
                    elementType: element.type,
                });
            }
        }
    }

    return Array.from(stereotypes.values());
}
